import type { RecordedRun } from '../contracts/game'

const DATABASE_NAME = 'neon-descent'
const STORE_NAME = 'replays'
const MAX_REPLAYS = 20

export interface StoredReplay {
  readonly id?: number
  readonly savedAt: number
  readonly run: RecordedRun
}

export class ReplayRepository {
  public async save(run: RecordedRun): Promise<boolean> {
    if (!('indexedDB' in globalThis)) return false
    try {
      const database = await this.open()
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite')
        const store = transaction.objectStore(STORE_NAME)
        store.add({ savedAt: Date.now(), run } satisfies StoredReplay)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(transaction.error ?? new Error('Replay transaction failed'))
      })
      await this.trim(database)
      database.close()
      return true
    } catch {
      return false
    }
  }

  public async list(): Promise<readonly StoredReplay[]> {
    if (!('indexedDB' in globalThis)) return []
    try {
      const database = await this.open()
      const values = await new Promise<StoredReplay[]>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readonly')
        const request = transaction.objectStore(STORE_NAME).getAll()
        request.onsuccess = () => resolve(request.result as StoredReplay[])
        request.onerror = () => reject(request.error ?? new Error('Replay listing failed'))
      })
      database.close()
      return values.sort((a, b) => b.savedAt - a.savedAt)
    } catch {
      return []
    }
  }

  public async delete(id: number): Promise<boolean> {
    if (!('indexedDB' in globalThis)) return false
    try {
      const database = await this.open()
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readwrite')
        transaction.objectStore(STORE_NAME).delete(id)
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(transaction.error ?? new Error('Replay deletion failed'))
      })
      database.close()
      return true
    } catch {
      return false
    }
  }

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, 1)
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
        }
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('Replay database failed to open'))
    })
  }

  private trim(database: IDBDatabase): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const keysRequest = store.getAllKeys()
      keysRequest.onsuccess = () => {
        const excess = keysRequest.result.slice(0, Math.max(0, keysRequest.result.length - MAX_REPLAYS))
        for (const key of excess) store.delete(key)
      }
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error ?? new Error('Replay cleanup failed'))
    })
  }
}
